// Hand-written content for the Sintra city page (/guides/portugal/sintra/). See lisbon.js for how the fields work.
// Facts: from the guides' own stop lists, or well-established history. No prices, hours or restaurants.

const sintra = {
  name: 'Sintra',
  aliases: ['Sintra'],
  heroImage: 'guide:pena',
  heroSample: 'historic',
  heroTagline: 'Palaces, a castle and a mountain of stories.',
  lead: "{n} self-guided audio tours for Sintra: Pena, Quinta da Regaleira, the Moorish Castle, Monserrate and the hills beyond. Play them on your phone, walk at your own pace, and ask the guide anything along the way.",

  themes: [
    ['start', 'The whole town'],
    ['palaces', 'Palaces & castle'],
    ['hills', 'Into the hills'],
  ],
  startHere: ['historic', 'pena', 'regaleira'],

  guides: [
    { key: 'historic', theme: 'start', title: 'Historic Centre of Sintra',
      versions: { en: 'historic-centre-of-sintra', pt: 'centro-historico-de-sintra' },
      blurb: 'The whole of Sintra in one guide: from the train to the town hall, the National Palace, the old town, Regaleira, Pena, the Moorish Castle, Monserrate and Seteais.' },

    { key: 'pena', theme: 'palaces', title: 'Pena Palace',
      versions: { pt: 'palacio-da-pena' },
      blurb: "Inside King Ferdinand II's palace on the hill: the Triton gate, the chapel, the cloister, the king's rooms, the Arab Room, the Stag Room, the kitchen and the park." },
    { key: 'regaleira', theme: 'palaces', title: 'Quinta da Regaleira',
      versions: { pt: 'quinta-da-regaleira' },
      blurb: "Carvalho Monteiro's estate of symbols: the Initiation Well, the Imperfect Well, the grottoes and tunnels, the tower, the chapel and the palace." },
    { key: 'mouros', theme: 'palaces', title: 'Moorish Castle',
      versions: { en: 'castelo-dos-mouros-moorish-castle', pt: 'castelo-dos-mouros' },
      blurb: "The castle on the ridge: the church that was a mosque, the walls, the Islamic quarter, the cistern, the Royal Tower and Ferdinand II's restorations." },
    { key: 'monserrate', theme: 'palaces', title: 'Monserrate Palace',
      versions: { en: 'palacio-de-monserrate' },
      blurb: "Byron, Beckford and the Cook family's neo-Gothic and Moorish palace: the music room, the octagonal hall and gardens planted from around the world." },
    { key: 'biester', theme: 'palaces', title: 'Biester Palace',
      versions: { en: 'the-biester-palace-experience' },
      blurb: "One of Sintra's lesser-known palaces: the family behind it, the entrance hall, the private lift, the music room, the chapel and the gardens." },

    { key: 'capuchos', theme: 'hills', title: 'Capuchos Convent and Peninha',
      versions: { en: 'capuchos-convent-and-peninha', pt: 'convento-dos-capuchos-e-peninha' },
      blurb: "Up in the hills: the tiny Capuchos convent and its cork-lined cells, Friar Honório's cave, the Peninha sanctuary and its view, and São Saturnino chapel." },
  ],

  areas: [
    { name: 'The old town', image: 'guide:historic', text: 'The National Palace, with its two great chimneys, stands in the middle of the old town, a short walk from the station.',
      guides: ['historic'] },
    { name: 'Pena & the Moorish Castle', text: 'Above the town, Pena Palace and the Moorish Castle share the same wooded ridge.',
      guides: ['pena', 'mouros'] },
    { name: 'The road to Seteais & Monserrate', image: 'guide:regaleira', text: 'West of the town, the road is lined with estates: Regaleira and Biester first, then Seteais, and further on Monserrate.',
      guides: ['regaleira', 'biester', 'monserrate'] },
    { name: 'The Sintra hills', text: 'Deep in the Sintra-Cascais Natural Park, the Capuchos convent and the Peninha sanctuary.',
      guides: ['capuchos'] },
  ],

  timeline: [
    { year: '800s', title: 'The Moorish Castle', text: 'A castle is built on the ridge above Sintra to watch over the land and the sea routes.', guide: 'mouros' },
    { year: '1560', title: 'The Capuchos convent', text: 'Franciscan friars found a tiny convent in the hills, lined with cork against the damp.', guide: 'capuchos' },
    { year: '1809', title: "Byron's 'glorious Eden'", text: 'Lord Byron visits Sintra and praises it in Childe Harold’s Pilgrimage.', guide: 'monserrate' },
    { year: '1838', title: 'Ferdinand II buys the hill', text: 'The king buys the ruined monastery on the summit and turns it into Pena Palace.', guide: 'pena' },
    { year: '1850s', title: 'Monserrate rebuilt', text: 'Francis Cook, an English merchant, rebuilds Monserrate and fills its park with plants from around the world.', guide: 'monserrate' },
    { year: '1904', title: 'Regaleira begins', text: 'António Augusto Carvalho Monteiro starts building his estate of wells, grottoes and symbols.', guide: 'regaleira' },
    { year: '1995', title: 'World Heritage', text: 'The Cultural Landscape of Sintra becomes a UNESCO World Heritage Site.', guide: 'historic' },
  ],
  timelineTitle: ['Sintra in ', 'seven moments'],

  plan: {
    title: ['Sintra in one or two days ', 'with our guides'],
    intro: 'Sintra is an easy day trip from Lisbon by train. One day covers the town and the hill; a second day reaches the estates and hills further out.',
    days: [
      { label: 'Morning', title: 'The town and Regaleira', stops: [['historic', 'start at the station'], ['regaleira', 'a short walk west']] },
      { label: 'Afternoon', title: 'Up the hill', stops: [['mouros', 'the walls first'], ['pena', 'then the palace']] },
      { label: 'Second day', title: 'Further out', stops: [['monserrate', 'palace and gardens'], ['capuchos', 'into the hills'], ['biester', 'or a quieter palace']] },
    ],
    dayTrip: { city: 'portugal/lisbon', text: 'Staying in Lisbon? The train to Sintra leaves from Rossio station.', link: 'See the Lisbon guides' },
    tips: [
      ['Take the train', 'Trains from Lisbon’s Rossio station reach Sintra in about 40 minutes.'],
      ['Start early', 'The palaces on the hill get busier as the day goes on.'],
      ['Bring a layer', 'The hills are often cooler and mistier than Lisbon, even in summer.'],
      ['Download before you go', 'Download your guides over Wi-Fi. They play outdoors with GPS and no mobile data.'],
    ],
  },

  extraFaq: [
    ['Can I visit Sintra as a day trip from Lisbon?', 'Yes. Trains from Lisbon’s Rossio station take about 40 minutes. One day covers the old town, Quinta da Regaleira, the Moorish Castle and Pena Palace.'],
  ],
  startAnswer: 'Start with the Historic Centre of Sintra: it begins at the station and covers the National Palace, Regaleira, Pena, the Moorish Castle, Monserrate and Seteais.',
};

export default sintra;
